import React, { useEffect, useRef, useState } from 'react';
import { sendInquiryEmail } from '../utils/inquiryEmail';
import * as inquiry from './inquirySubmission';


const isValidEmail = (email) => {
  const value = (email || '').trim();
  if (!value) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(value);
};

const digitsOnly = (value) => (value || '').replace(/\D/g, '');

const preventNonNumeric = (event) => {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
  ];

  if (event.ctrlKey || event.metaKey) return;
  if (allowedKeys.includes(event.key)) return;
  if (!/^\d$/.test(event.key)) event.preventDefault();
};

const getInitialFormData = (defaultSubject = '', defaultCountryCode = inquiry.DEFAULT_COUNTRY_CODE) => ({
  name: '',
  email: '',
  countryCode: defaultCountryCode,
  phone: '',
  subject: defaultSubject,
  currentEducation: '',
  interestedIn: '',
  message: '',
});

const InquiryPopupForm = ({
  isOpen,
  onClose,
  defaultSubject = '',
  formTitle = 'Contact Us',
  formSubtitle = 'Share your requirement and our team will get back to you.',
  trackingFormName = 'SwiftMaze Popup Inquiry Form',
  formVariant = 'default',
  defaultCountryCode = inquiry.DEFAULT_COUNTRY_CODE,
}) => {
  const isTrainingForm = formVariant === 'training';
  const formCopy = isTrainingForm
    ? {
        kicker: 'Training Inquiry',
        nameLabel: 'Student / learner name',
        namePlaceholder: 'Enter student name',
        phoneLabel: 'Student phone number *',
        emailLabel: 'Email address',
        emailPlaceholder: 'student@example.com',
        subjectLabel: 'Course / internship interest',
        subjectPlaceholder: 'Course or internship topic',
        educationLabel: 'Current education / background',
        educationPlaceholder: 'Select your current background',
        interestedInLabel: 'Interested in',
        interestedInPlaceholder: 'Select inquiry type',
        messageLabel: 'Learning goal / project requirement *',
        messagePlaceholder:
          'Share your current education, course interest, preferred mode, or project stage',
        submitLabel: 'Request Guidance',
        success:
          'Thank you. Your training inquiry has been received. Our team will guide you shortly.',
      }
    : {
        kicker: 'Quick Inquiry',
        nameLabel: 'Your name',
        namePlaceholder: 'Enter your name',
        phoneLabel: 'Phone number *',
        emailLabel: 'Email address',
        emailPlaceholder: 'you@example.com',
        subjectLabel: 'Subject',
        subjectPlaceholder: 'Enter subject',
        messageLabel: 'Your Message *',
        messagePlaceholder: 'Write your requirement',
        submitLabel: 'Send Now',
        success: 'Thank you. Your inquiry has been received. We will get back to you shortly.',
      };
  const [formData, setFormData] = useState(getInitialFormData(defaultSubject, defaultCountryCode));
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ success: '', error: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const wasOpenRef = useRef(false);

  const resetForm = () => {
    setStatus({ success: '', error: '' });
    setFieldErrors({});
    setFormData(getInitialFormData(defaultSubject, defaultCountryCode));
  };

  const handlePopupClose = () => {
    if (loading) return;
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return undefined;
    }

    if (!wasOpenRef.current) {
      resetForm();
      wasOpenRef.current = true;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEsc = (event) => {
      if (event.key === 'Escape' && !loading) handlePopupClose();
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, loading]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'countryCode') {
      const nextCode = value;
      const rule = inquiry.getCountryPhoneRule(nextCode);

      setFormData((prev) => ({
        ...prev,
        countryCode: nextCode,
        phone: digitsOnly(prev.phone).slice(0, rule.maxDigits),
      }));
      return;
    }

    if (name === 'phone') {
      const code = formData.countryCode || inquiry.DEFAULT_COUNTRY_CODE;
      const rule = inquiry.getCountryPhoneRule(code);

      setFormData((prev) => ({
        ...prev,
        phone: digitsOnly(value).slice(0, rule.maxDigits),
      }));
      return;
    }

    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const code = formData.countryCode || inquiry.DEFAULT_COUNTRY_CODE;
    const phoneDigits = digitsOnly(formData.phone);

    if (formData.email.trim() && !isValidEmail(formData.email)) {
      nextErrors.email = 'Please enter a valid email address.';
    }

    if (!phoneDigits) {
      nextErrors.phone = 'Please enter your mobile number.';
    } else if (!inquiry.isValidPhoneNumber(code, phoneDigits)) {
      nextErrors.phone = inquiry.getPhoneValidationMessage(code);
    }

    if (!formData.message.trim()) {
      nextErrors.message = 'Please enter your message.';
    }

    setFieldErrors(nextErrors);
    return { isValid: Object.keys(nextErrors).length === 0, code, phoneDigits };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setStatus({ success: '', error: '' });
    const { isValid, code, phoneDigits } = validateForm();

    if (!isValid) {
      setStatus({ success: '', error: 'Please correct the highlighted fields and try again.' });
      setLoading(false);
      return;
    }

    const fullPhone = `${inquiry.getDialCode(code)}${phoneDigits}`;

    try {
      await sendInquiryEmail({
        formName: trackingFormName,
        name: formData.name,
        email: formData.email,
        phone: fullPhone,
        subject: formData.subject,
        currentEducation: formData.currentEducation,
        interestedIn: formData.interestedIn,
        message: formData.message,
      });

      // Telegram submission kept for future use. Email submission is active now.
      // const botToken = '7869975126:AAETFptfICnYosInsBlMFWlfOpeeGAl_s10';
      // const chatId = '-1002662497260';
      // await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ chat_id: chatId, text: msg, parse_mode: 'Markdown' }),
      // });

      setStatus({
        success: formCopy.success,
        error: '',
      });
      setFieldErrors({});
      setFormData(getInitialFormData(defaultSubject));
    } catch (error) {
      console.error('Error sending inquiry email:', error);
      setStatus({
        success: '',
        error: 'Something went wrong. Please check your internet connection and try again later.',
      });
    }

    setLoading(false);
  };
  if (!isOpen) return null;

  return (
    <div className="inquiry-popup-overlay" onClick={loading ? undefined : handlePopupClose}>
      <div
        className={`inquiry-popup-dialog ${
          isTrainingForm ? 'inquiry-popup-dialog-training' : ''
        }`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="inquiry-popup-title"
      >
        <button
          type="button"
          className="inquiry-popup-close"
          onClick={handlePopupClose}
          aria-label="Close form"
          disabled={loading}
        >
          <span className="fa fa-times"></span>
        </button>

        <div className="inquiry-popup-head">
          <span className="inquiry-popup-kicker">{formCopy.kicker}</span>
          <h3 id="inquiry-popup-title">{formTitle}</h3>
          <p>{formSubtitle}</p>
        </div>

        <div className="contact-form inquiry-popup-form">
          {status.success && <div className="inquiry-popup-alert inquiry-popup-success">{status.success}</div>}
          {status.error && <div className="inquiry-popup-alert inquiry-popup-error">{status.error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="row clearfix">
              <div className="form-group col-lg-6 col-md-6 col-sm-12">
                <label>{formCopy.nameLabel}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  name="name"
                  placeholder={formCopy.namePlaceholder}
                  autoComplete="name"
                />
              </div>

              <div className="form-group col-lg-6 col-md-6 col-sm-12">
                <label>{formCopy.phoneLabel}</label>
                <div className="inquiry-popup-phone-row">
                  <input
                    key={formData.countryCode}
                    type="text"
                    list={inquiry.COUNTRY_DATALIST_ID}
                    aria-label="Country code"
                    className="inquiry-popup-country country-phone-search"
                    {...inquiry.createCountryInputHandlers({
                      countryCode: formData.countryCode,
                      onCountryChange: (value) => handleChange({ target: { name: 'countryCode', value } }),
                    })}
                  />
                  {inquiry.renderCountryPhoneDatalist()}

                  <input
                    type="tel"
                    name="phone"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder={
                      inquiry.getCountryPhoneRule(formData.countryCode).placeholder
                    }
                    value={formData.phone}
                    onChange={handleChange}
                    onKeyDown={preventNonNumeric}
                    maxLength={inquiry.getCountryPhoneRule(formData.countryCode).maxDigits}
                    required
                  />
                </div>
                {fieldErrors.phone && <div style={{ color: '#d93025', fontSize: '14px', marginTop: '8px' }}>{fieldErrors.phone}</div>}
              </div>

              <div className="form-group col-lg-6 col-md-6 col-sm-12">
                <label>{formCopy.emailLabel}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  name="email"
                  placeholder={formCopy.emailPlaceholder}
                  autoComplete="email"
                />
                {fieldErrors.email && <div style={{ color: '#d93025', fontSize: '14px', marginTop: '8px' }}>{fieldErrors.email}</div>}
              </div>

              <div className="form-group col-lg-6 col-md-6 col-sm-12">
                <label>{formCopy.subjectLabel}</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={handleChange}
                  name="subject"
                  placeholder={formCopy.subjectPlaceholder}
                />
              </div>

              {isTrainingForm && (
                <>
                  <div className="form-group col-lg-6 col-md-6 col-sm-12">
                    <label>{formCopy.educationLabel}</label>
                    <select
                      value={formData.currentEducation}
                      onChange={handleChange}
                      name="currentEducation"
                    >
                      <option value="">{formCopy.educationPlaceholder}</option>
                      <option value="BCA">BCA</option>
                      <option value="B.Tech / BE">B.Tech / BE</option>
                      <option value="Diploma">Diploma</option>
                      <option value="12th / HSC">12th / HSC</option>
                      <option value="Working Professional">Working Professional</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group col-lg-6 col-md-6 col-sm-12">
                    <label>{formCopy.interestedInLabel}</label>
                    <select
                      value={formData.interestedIn}
                      onChange={handleChange}
                      name="interestedIn"
                    >
                      <option value="">{formCopy.interestedInPlaceholder}</option>
                      <option value="Course Training">Course Training</option>
                      <option value="Internship">Internship</option>
                      <option value="Project Guidance">Project Guidance</option>
                      <option value="Industrial Training">Industrial Training</option>
                    </select>
                  </div>
                </>
              )}

              <div className="form-group col-lg-12 col-md-12 col-sm-12">
                <label>{formCopy.messageLabel}</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={formCopy.messagePlaceholder}
                  required
                ></textarea>
                {fieldErrors.message && <div style={{ color: '#d93025', fontSize: '14px', marginTop: '8px' }}>{fieldErrors.message}</div>}
              </div>

              <div className="form-group text-center col-lg-12 col-md-12 col-sm-12">
                <button className="theme-btn btn-style-three" type="submit" disabled={loading}>
                  <span className="txt">{loading ? 'Sending...' : formCopy.submitLabel}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InquiryPopupForm;


